import { z } from 'zod';

// Имя мерчанта — только латиница, цифры и пробел. Никаких «%», «&», кавычек,
// подчёркиваний, иероглифов и диакритики (правило Глеба 2026-07-27).
//
// Почему это контракт, а не «пожелание к данным»:
//   • имя едет в отгрузочную таблицу (Merchant_Name) и в ИМЯ ФАЙЛА, а имя
//     файла склеивается через подчёркивание —
//     `{PID}_IMG_{####}_{Pass_Type}_{Merchant}_{N}`. Мерчант вида `H_M` или
//     `Meetup_Zurich_Together` ломает разбор имени у принимающей стороны;
//   • `% Arabica` уехало в отгрузку и вернулось отказом (2026-07-27);
//   • нелатинское имя (`すかいらーく coupon`) заказчик прочитать не может.
//
// Нормализация не «чинит» плохое имя молча: если после чистки латинских букв
// не осталось, возвращается пустая строка — то есть мерчант считается
// НЕИЗВЕСТНЫМ, и кадром занимается крон `merchant-gate` (vision → доп.
// проверка QA). Пустое имя честнее огрызка вроде «coupon».

const APOSTROPHES = /['‘’ʼ`´]/g;

export function normalizeMerchantName(raw: string): string {
  const deaccented = String(raw || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(APOSTROPHES, '');
  if (/\p{Letter}/u.test(deaccented.replace(/[A-Za-z]/g, ''))) return '';
  const stripped = deaccented
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return /[A-Za-z]/.test(stripped) ? stripped : '';
}

export function merchantNameOk(raw: string): boolean {
  const s = String(raw || '');
  return s.length > 0 && s === normalizeMerchantName(s);
}

/** Схема поля: принимает сырое имя, отдаёт нормализованное. */
export const MerchantNameSchema = z.string().transform(normalizeMerchantName);

/** Схема для мест, где имя ОБЯЗАНО быть уже нормализованным (отгрузка). */
export const StrictMerchantNameSchema = z.string().refine(merchantNameOk, {
  message: 'merchant must be latin letters, digits and spaces only',
});

// ─── КЛЮЧ СОВПАДЕНИЯ мерчанта ───────────────────────────────────────────────
//
// Это НЕ имя (см. normalizeMerchantName выше — то, что едет в отгрузку). Это
// ключ, по которому «Burger King», «burgerking» и «BURGER-KING» считаются
// одним мерчантом при подсчёте капа 20.
//
// ЗАЧЕМ ОДНА ФУНКЦИЯ. До 2026-08-14 канон существовал в двух несовместимых
// видах: JS `canonMerchant` (lower+trim+схлопнуть пробелы — пробел ОСТАВАЛСЯ)
// и SQL-цепочка REPLACE (пробел ВЫРЕЗАЛСЯ). Гейт связывал JS-значение
// параметром с SQL-выражением по колонке, поэтому для любого имени с пробелом
// или пунктуацией совпадение было НЕВОЗМОЖНО. «massimo dutti» в es_ES: гейт
// видел 0, в отгрузке лежало 20 — мерчант закрыт, а сбор продолжался.
// 2 085 пар из 4 041 были слепы; 86 уже полных пар гейт показывал как 0/20.
//
// ЗЕРКАЛО ОБЯЗАНО БЫТЬ ТОЧНЫМ, и наивное им не является — SQLite отличается
// от JS в двух местах, и оба всплывают на наших же данных:
//   • LOWER() в SQLite — ТОЛЬКО ASCII. LOWER('MÉDANO') = 'mÉdano', а
//     'MÉDANO'.toLowerCase() = 'médano'. У нас таких имён полно (médano,
//     điện máy xanh, японские и тайваньские мерчанты);
//   • TRIM() в SQLite режет ТОЛЬКО пробелы, а String.trim() — любой пробельный
//     символ, включая tab и перевод строки.
// Поэтому ниже — ASCII-lower и trim ровно по пробелу, а не встроенные методы.

/** Символы, вырезаемые из ключа. Порядок не важен, состав — важен. */
const KEY_STRIP = ["_", "-", "&", ".", "'", " ", "/"] as const;

/** ASCII-only понижение регистра — как LOWER() в SQLite. */
function asciiLower(s: string): string {
  let out = '';
  for (const ch of s) {
    const c = ch.charCodeAt(0);
    out += (c >= 65 && c <= 90) ? String.fromCharCode(c + 32) : ch;
  }
  return out;
}

/** Обрезка ровно по пробелу — как TRIM() в SQLite (не String.trim()). */
function sqlTrim(s: string): string {
  let a = 0, b = s.length;
  while (a < b && s[a] === ' ') a++;
  while (b > a && s[b - 1] === ' ') b--;
  return s.slice(a, b);
}

/**
 * Ключ совпадения мерчанта. ЕДИНСТВЕННАЯ функция для сравнения имён —
 * и она обязана давать ровно то же, что `MERCHANT_KEY_SQL` по колонке.
 * Инвариант закреплён тестом на реальных именах и сверкой с живой базой
 * (`pnpm run merchant:canon-drift`).
 */
export function merchantKey(raw: string): string {
  let s = asciiLower(sqlTrim(String(raw || '')));
  for (const ch of KEY_STRIP) s = s.split(ch).join('');
  return s;
}

/**
 * То же преобразование как SQL-выражение. `$X` заменяется на имя колонки:
 *   MERCHANT_KEY_SQL.replace('$X', 'merchant')
 * Держится РЯДОМ с merchantKey намеренно: разъехаться они могут только если
 * кто-то правит одну строку и не правит соседнюю — а это видно в диффе.
 */
export const MERCHANT_KEY_SQL =
  "REPLACE(REPLACE(REPLACE(REPLACE(REPLACE(REPLACE(REPLACE(LOWER(TRIM($X)),'_',''),'-',''),'&',''),'.',''),'''',''),' ',''),'/','')";

/**
 * Латинская часть имени для ПУТИ ОБОГАЩЕНИЯ (не для валидации).
 *
 * `normalizeMerchantName` по контракту возвращает '' на любом имени, где после
 * снятия диакритики осталась нелатинская буква, — и это правильно для гейта
 * упаковки: в отгрузку не должно уехать «CGV 강남». Но на пути AI-обогащения то
 * же правило работало как молчаливая потеря: модель отвечала «예술의전당» или
 * «CGV 강남», нормализатор обнулял ответ целиком, попытка списывалась, и после
 * трёх заходов кадр оставался без мерчанта навсегда (1 358 таких кадров на
 * 2026-08-25).
 *
 * Здесь мы спасаем латинскую часть, если она сама по себе осмысленна
 * (≥2 латинских букв): «CGV 강남» → «CGV». Если латиницы нет вовсе — '',
 * как и раньше.
 */
export function latinizeMerchantName(raw: string): string {
  const direct = normalizeMerchantName(raw);
  if (direct) return direct;
  const latinOnly = String(raw || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(APOSTROPHES, '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const letters = latinOnly.replace(/[^A-Za-z]/g, '');
  return letters.length >= 2 ? latinOnly : '';
}

// ─── КЛЮЧ КАПА мерчанта (ADR-0094) ──────────────────────────────────────────
//
// ОТДЕЛЬНЫЙ ключ для семьи капа: всё, у чего SQL-сторона сравнения —
// `pothos_assets.merchant` или `merchant_caps` (реестр, гейт упаковки, слоты
// дизайнеров, скрейпер, проверщик манифестов, ключевики). `merchantKey` выше
// остаётся у остальных потребителей: их колонки (`shipped_assets`,
// `registrations`, `research_merchants.aggregator`, `gc_merchant`) пишутся
// сырыми, и смена ключа тихо развела бы там JS и SQL стороны.
//
// ЗАЧЕМ. `merchantKey` не снимает диакритику и знаки вне KEY_STRIP, поэтому
// один бренд лежал в двух ячейках капа и набирал сверх 20: `El Corte Ingles` +
// `El Corte Inglés` в одной локали, `go!`/`Go`, `pressbyrån kompis`/
// `Pressbyran Kompis` (решение владельца 2026-09-11).
//
// ПОЧЕМУ ТАБЛИЦА, а не «любая диакритика» в SQL. D1 режет глубину выражения
// на 100: цепочка REPLACE на всю латиницу обеих форм регистра (LOWER в SQLite —
// только ASCII) туда не лезет. Набор символов в колонке при этом конечен и
// известен: отгруженные строки заморожены (имя вшито в имена файлов архивов),
// а все новые записи идут через `catalogMerchantName` ниже и выходят чистой
// латиницей. Таблица = символы, реально лежащие в колонке; сверка
// `pnpm run merchant:canon-drift` ловит новый символ до того, как он разведёт
// ячейки. Расхождение = строка в CAP_FOLD.
//
// `capMerchantKey` для СЫРОГО ввода шире таблицы (любая латинская диакритика,
// любые знаки). На хранимых именах обе стороны обязаны совпадать — это
// закрепляют тест на снимке живых имён и сверка с базой.

/** Пары «что → на что» в порядке применения. Сущности раньше `&`, `#`, `;`. */
export const CAP_FOLD: ReadonlyArray<readonly [string, string]> = [
  ['&amp;', ''], ['&#039;', ''], ['&#39;', ''], ['&quot;', ''],
  ['à', 'a'], ['á', 'a'], ['â', 'a'], ['ã', 'a'], ['ä', 'a'], ['å', 'a'], ['ā', 'a'],
  ['Á', 'a'], ['Ä', 'a'], ['Å', 'a'],
  ['ç', 'c'],
  ['è', 'e'], ['é', 'e'], ['ê', 'e'], ['ệ', 'e'], ['É', 'e'],
  ['í', 'i'], ['ï', 'i'], ['Í', 'i'],
  ['ñ', 'n'], ['Ñ', 'n'],
  ['ó', 'o'], ['ô', 'o'], ['ö', 'o'], ['ø', 'o'], ['ō', 'o'], ['Ô', 'o'], ['Ö', 'o'], ['Ø', 'o'],
  ['ú', 'u'], ['ü', 'u'],
  ['đ', 'd'],
  ...[' ', '_', '-', '&', '.', "'", '/', '!', '"', '#', '%', '(', ')', '*', '+', ',',
      ':', ';', '@', '[', ']', '|', '–', '—', '’', '™', '・']
    .map((c) => [c, ''] as const),
];

const sqlLit = (s: string): string => "'" + s.split("'").join("''") + "'";

/**
 * Ключ капа как SQL-выражение: `CAP_MERCHANT_KEY_SQL.replace('$X', 'merchant')`.
 * Собирается из CAP_FOLD — вторая сторона правится ТОЛЬКО через таблицу.
 */
export const CAP_MERCHANT_KEY_SQL: string = CAP_FOLD.reduce(
  (e, [from, to]) => `REPLACE(${e},${sqlLit(from)},${sqlLit(to)})`,
  'LOWER(TRIM($X))',
);

/** Латинские буквы, которые NFD не раскладывает, а свернуть их надо. */
const LATIN_SPECIAL: Record<string, string> = {
  'đ': 'd', 'Đ': 'd', 'ø': 'o', 'Ø': 'o', 'ł': 'l', 'Ł': 'l', 'ß': 'ss',
  'æ': 'ae', 'Æ': 'ae', 'œ': 'oe', 'Œ': 'oe', 'ı': 'i',
};

function decodeEntities(s: string): string {
  return s.replace(/&amp;/gi, '&').replace(/&#0?39;/g, "'").replace(/&quot;/gi, '"');
}

/**
 * Снимает диакритику ТОЛЬКО с латинских букв. Нелатинские письменности не
 * трогаются: NFD разложил бы корейские слоги на чамо и снял бы японский
 * дакутэн (が → か), а SQL-сторона их не трогает — ключи разошлись бы, а разные
 * бренды склеились.
 */
function foldLatin(s: string): string {
  let out = '';
  let prevLatin = false;
  for (const ch of s.normalize('NFC')) {
    if (/\p{M}/u.test(ch)) {
      if (!prevLatin) out += ch;
      continue;
    }
    const special = LATIN_SPECIAL[ch];
    if (special !== undefined) {
      out += special;
      prevLatin = true;
    } else if (/\p{Script=Latin}/u.test(ch)) {
      out += ch.normalize('NFD').replace(/\p{M}/gu, '');
      prevLatin = true;
    } else {
      out += ch;
      prevLatin = false;
    }
  }
  return out;
}

/**
 * Ключ капа мерчанта для СЫРОГО имени. Совпадает с `CAP_MERCHANT_KEY_SQL` на
 * любом имени из колонки каталога (тест + `merchant:canon-drift`).
 *   `El Corte Inglés` = `EL-CORTE-INGLES` → `elcorteingles`
 *   `go!` = `Go` → `go`;  `すかいらーく` → `すかいらーく`
 */
export function capMerchantKey(raw: string): string {
  const folded = foldLatin(decodeEntities(String(raw || '')));
  return asciiLower(folded).replace(/[^\p{L}\p{M}\p{N}]+/gu, '');
}

/**
 * Имя мерчанта ДЛЯ КАТАЛОГА — единственная функция, через которую сырое имя
 * попадает в `pothos_assets.merchant`.
 *
 * ЗАЧЕМ (Глеб, звонок 2026-09-02: «нормализацию надо делать на старте»).
 * Апостроф и амперсанд законны для бренда и запрещены в отгрузке (имя едет в
 * ИМЯ ФАЙЛА), поэтому `mcdonald's`, `h&m` доезжали до `accepted` и вставали
 * навсегда под ярлыком «мерчант не латиницей».
 *
 * ═══ ИНВАРИАНТ: ФУНКЦИЯ НЕ ДВИГАЕТ КЛЮЧ КАПА ═══
 *
 * `merchant_caps` — таблица ПРОИЗВОДНАЯ от `pothos_assets` (ячейка кладётся под
 * `CAP_MERCHANT_KEY_SQL` по колонке), а читатели капа передают сырое имя и
 * считают `capMerchantKey` в JS. Ключ хранимого имени обязан совпадать с ключом
 * сырого — иначе бренд набирает до 2×20, и ни одна ячейка не закрывается.
 *
 * До 2026-09-11 ключ не снимал диакритику, и функция имела право чистить только
 * KEY_STRIP: `beauté privée`, `go!`, `% arabica`, `điện máy xanh` оставались
 * заблокированными без владельца (долг миграции 0127). Ключ капа теперь сам
 * сворачивает латиницу и знаки — эти имена чинятся без сдвига ключа.
 *
 * Чиним ТОЛЬКО ЕСЛИ ЧИНИТСЯ ДО КОНЦА: результат обязан пройти гейт отгрузки и
 * сохранить ключ капа. Иначе (нелатинская письменность, `!!!`) возвращается
 * исходное имя — оно стоит на гейте ВИДИМЫМ, а не превращается в огрызок.
 *
 * Длину НЕ режем: предел упаковки `MERCHANT_MAX_LEN = 40`
 * (manager/pack-vocab.ts), и обрезка тоже сдвинула бы ключ.
 */
export function catalogMerchantName(raw: string): string {
  const input = String(raw || '').trim();
  let s = asciiLower(foldLatin(decodeEntities(input)));
  // Апостроф СКЛЕИВАЕТ: «mcdonald's» → «mcdonalds», а не «mcdonald s».
  s = s.replace(APOSTROPHES, '');
  // Остальные знаки разделяют: «h&m» → «h m», «go!» → «go».
  s = s.replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ').replace(/ +/g, ' ').trim();
  return merchantNameOk(s) && capMerchantKey(s) === capMerchantKey(input) ? s : input;
}
